import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { Component } from '@theme/component';
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

const CART_COUNTDOWN_STORAGE_KEY = 'cart-drawer-countdown-ends-at';
const CART_COUNTDOWN_DEFAULT_MS = 20 * 60 * 1000;

/**
 * @param {number} [durationMs]
 * @returns {number}
 */
function getCountdownDuration(durationMs) {
  return Number.isFinite(durationMs) && durationMs > 0 ? durationMs : CART_COUNTDOWN_DEFAULT_MS;
}

/**
 * @returns {number | null}
 */
function readCountdownEndsAt() {
  try {
    const raw = sessionStorage.getItem(CART_COUNTDOWN_STORAGE_KEY);
    if (!raw) return null;

    const endsAt = Number(raw);
    return Number.isFinite(endsAt) ? endsAt : null;
  } catch {
    return null;
  }
}

/**
 * @param {number} endsAt
 */
function writeCountdownEndsAt(endsAt) {
  try {
    sessionStorage.setItem(CART_COUNTDOWN_STORAGE_KEY, String(endsAt));
  } catch {
    // sessionStorage may be unavailable.
  }
}

function clearCountdownEndsAt() {
  try {
    sessionStorage.removeItem(CART_COUNTDOWN_STORAGE_KEY);
  } catch {
    // sessionStorage may be unavailable.
  }
}

/**
 * Starts a countdown only when one is not already running for this tab.
 * @param {number} [durationMs]
 * @returns {number}
 */
function ensureCountdownEndsAt(durationMs = CART_COUNTDOWN_DEFAULT_MS) {
  const existing = readCountdownEndsAt();
  const now = Date.now();

  if (existing != null && existing > now) {
    return existing;
  }

  const endsAt = now + getCountdownDuration(durationMs);
  writeCountdownEndsAt(endsAt);
  return endsAt;
}

/**
 * Restarts the countdown from a fresh duration.
 * @param {number} [durationMs]
 * @returns {number}
 */
function restartCountdownEndsAt(durationMs = CART_COUNTDOWN_DEFAULT_MS) {
  const endsAt = Date.now() + getCountdownDuration(durationMs);
  writeCountdownEndsAt(endsAt);
  return endsAt;
}

/**
 * @param {number} remainingMs
 * @returns {string}
 */
function formatCountdown(remainingMs) {
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number }, data?: { itemCount?: number } }>} event
   */
  #handleCartAdd = (event) => {
    const itemCount = event.detail.resource?.item_count ?? event.detail.data?.itemCount;

    if (typeof itemCount === 'number' && itemCount <= 0) {
      clearCountdownEndsAt();
    } else if (typeof itemCount === 'number' && itemCount > 0) {
      ensureCountdownEndsAt();
    }

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(itemCount);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

/**
 * Tab-scoped 20-minute cart reservation countdown for the drawer header.
 *
 * @typedef {object} CountdownRefs
 * @property {HTMLTimeElement} time
 *
 * @extends {Component<CountdownRefs>}
 */
class CartDrawerCountdown extends Component {
  requiredRefs = ['time'];

  /** @type {number | null} */
  #intervalId = null;

  /** @type {number} */
  #endsAt = 0;

  get #durationMs() {
    return getCountdownDuration(Number(this.dataset.durationMs));
  }

  connectedCallback() {
    super.connectedCallback();
    this.#endsAt = ensureCountdownEndsAt(this.#durationMs);
    this.#tick();
    this.#intervalId = window.setInterval(() => this.#tick(), 1000);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#intervalId != null) {
      window.clearInterval(this.#intervalId);
      this.#intervalId = null;
    }
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  /**
   * @param {CustomEvent<{ resource?: { item_count?: number }, data?: { itemCount?: number } }>} event
   */
  #handleCartUpdate = (event) => {
    const itemCount = event.detail.resource?.item_count ?? event.detail.data?.itemCount;

    if (typeof itemCount === 'number' && itemCount <= 0) {
      clearCountdownEndsAt();
      return;
    }

    if (typeof itemCount === 'number' && itemCount > 0) {
      this.#endsAt = ensureCountdownEndsAt(this.#durationMs);
      this.#tick();
    }
  };

  #tick = () => {
    const remainingMs = this.#endsAt - Date.now();

    if (remainingMs <= 0) {
      this.#endsAt = restartCountdownEndsAt(this.#durationMs);
    }

    const displayMs = Math.max(0, this.#endsAt - Date.now());
    const label = formatCountdown(displayMs);
    const totalSeconds = Math.max(0, Math.ceil(displayMs / 1000));

    this.refs.time.textContent = label;
    this.refs.time.dateTime = `PT${totalSeconds}S`;
  };
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

if (!customElements.get('cart-drawer-countdown')) {
  customElements.define('cart-drawer-countdown', CartDrawerCountdown);
}
